import { get } from 'lodash-es'
import { getErrorMessage, WEBTEST_URL } from '@/main'
import Axios from 'axios'
import { getChemicalUniqueKey, prepareChemicals } from '@/utils/chemical'
import {
  constructErrorResultsForEachChemical,
  constructValidationErrorResultsForEachChemical,
  defaultChemicalKey,
  enrichResponseWithSentStructures,
  partitionChemicalsByValidation,
} from '@/utils/responseNormalizers'
import { modelStateRegistry } from '@/utils/modelStateRegistry'

const webtestState = get(modelStateRegistry, 'webtest')

const state = {
  ...webtestState.createState(),
}

const getters = {
  ...webtestState.createGetters(),
}

const mutations = {
  ...webtestState.createMutations(),
}

const keys = webtestState.keys
const getCurrentResults = (getters) => getters[keys.getResults]

const resultsKey = 'chemicals'

const actions = {
  async predict(context, { structures, batchSize = 200 } = {}) {
    const { state, commit, getters } = context
    commit(keys.RESET_STATE)

    const { validChemicals, invalidChemicalsWithError } = partitionChemicalsByValidation(structures)

    const validationErrorResults = constructValidationErrorResultsForEachChemical({
      invalidChemicalsWithError,
      resultsKey,
      chemicalKey: defaultChemicalKey,
    })

    commit(keys.PUSH_RESULTS, validationErrorResults)

    if (!validChemicals?.length) {
      return getCurrentResults(getters)
    }

    commit(keys.SET_LOADING, true)

    const abortController = new AbortController()
    commit(keys.SET_ABORT_CONTROLLER, abortController)

    try {
      for (let i = 0; i < validChemicals.length; i += batchSize) {
        if (abortController.signal.aborted) break

        const batch = validChemicals.slice(i, i + batchSize)

        await this._vm.$axios
          .post(
            `${WEBTEST_URL}/predict`,
            {
              chemicals: prepareChemicals(batch, getChemicalUniqueKey),
              report: 'HTML',
            },
            { signal: abortController.signal },
          )
          .then((response) => {
            const enrichedResponse = enrichResponseWithSentStructures({
              responseData: response.data,
              sentStructures: structures,
              resultsKey,
              chemicalKey: defaultChemicalKey,
              getSentChemicalIdentifier: getChemicalUniqueKey,
              getReceivedChemicalIdentifier: (chemical) => chemical?.id,
            })
            commit(keys.PUSH_RESULTS, enrichedResponse)
          })
          .catch((fetchError) => {
            if (fetchError.name === 'AbortError' || fetchError.name === 'CanceledError') return
            console.error('WebTEST predict error:', fetchError)
            const networkErrorResults = constructErrorResultsForEachChemical({
              sentStructures: batch,
              errorMessage: getErrorMessage(fetchError),
              chemicalKey: defaultChemicalKey,
              resultsKey,
            })
            commit(keys.PUSH_RESULTS, networkErrorResults)
          })
      }
    } finally {
      commit(keys.SET_LOADING, false)
      commit(keys.SET_ABORT_CONTROLLER, null)
    }

    return getCurrentResults(getters)
  },
}

export function prepareWebtestMetadata(context) {
  const { commit } = context
  return Axios.get(`${WEBTEST_URL}/metadata`)
    .then((response) => {
      commit(`webtest/${keys.SET_METADATA}`, response.data)
      commit('setFeatures', { webtest: { category: 'module' } })
    })
    .catch((error) => {
      console.warn('WEBTEST module is not available: %o', getErrorMessage(error))
      commit('removeFeature', { webtest: {} })
    })
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
